import React, { useEffect, useState } from "react";
import "./index.scss";
import { Box, Select, MenuItem, Typography } from "@mui/material";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import AsyncLocalStorage from "@createnextapp/async-local-storage";
import { useMyBranches, myBranchesKeys } from "api/queries/useMyBranches";
import { branchKeys } from "api/queries/useBranch";
import { staffKeys } from "api/queries/useStaff";
import { queryClient } from "utilities/queryClient";
import { getUserPermissions } from "../../../store/actions/aclUserPermissions";
import { getUser } from "../../../store/actions/user";

/**
 * Lets a staff member who works at more than one branch (same login shared
 * across branches) switch which branch's data/identity they're acting as.
 * Renders nothing for the common case of a staff member with just one branch.
 *
 * Reuses the same "adminBranch" AsyncLocalStorage key (and axios interceptor)
 * as BranchSelect -- the backend resolves ?branch_id= against this user's own
 * staff rows first, so no separate plumbing is needed.
 */
function MyBranchSwitcher() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: branches } = useMyBranches();
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [currentLocation, setCurrentLocation] = useState("");

  useEffect(() => {
    async function initialFunc() {
      const stored = await AsyncLocalStorage.getItem("adminBranch");
      if (stored) {
        setSelectedBranchId(stored);
      } else if (branches && branches.length) {
        const current = branches.find((b) => b.is_current);
        setSelectedBranchId(String((current || branches[0]).branch_id));
      }
    }
    initialFunc();
  }, [branches]);

  useEffect(() => {
    setCurrentLocation(location.pathname);
  }, [location.pathname]);

  if (!branches || branches.length <= 1) {
    return null;
  }

  const onBranchChange = async (value) => {
    try {
      await AsyncLocalStorage.setItem("adminBranch", value);
      setSelectedBranchId(value);
      // Permissions (and everything derived from them -- the sidebar, branch
      // data, subscriptions) are only fetched once on login, not on
      // navigation, so switching branches here would otherwise leave the
      // sidebar showing the previous branch's permissions until a full
      // reload. Re-fetching explicitly updates state.aclUserPermissions,
      // which cascades through App.js's own effect into branch/loyalty/
      // client/subscription data too.
      dispatch(getUserPermissions());
      // state.user carries branch-dependent fields (e.g. staff_attendance_punch),
      // and useBranch/useStaff/useBasicStaff are cached by React Query with no
      // branch dimension in their query keys -- the axios interceptor changes
      // ?branch_id= transparently at the HTTP layer, so without this React
      // Query has no way to know the previous branch's cached data is now
      // stale, and getUser() would otherwise only ever run once at mount.
      dispatch(getUser());
      queryClient.invalidateQueries({ queryKey: myBranchesKeys.all });
      queryClient.invalidateQueries({ queryKey: branchKeys.all });
      queryClient.invalidateQueries({ queryKey: staffKeys.all });
      history.replace("/pageload");
      setTimeout(() => {
        history.replace(currentLocation);
      }, 1000);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="my-branch-switcher">
      <Box>
        <Select
          displayEmpty
          value={selectedBranchId || ""}
          onChange={(e) => onBranchChange(e.target.value)}
          size="small"
          renderValue={(selected) => {
            const found = branches.find((b) => String(b.branch_id) === String(selected));
            return found ? found.branch_name : "Select Branch";
          }}
        >
          {branches.map((branch) => (
            <MenuItem key={branch.staff_id} value={String(branch.branch_id)}>
              <Box>
                <Typography variant="body2">{branch.branch_name}</Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Box>
    </div>
  );
}
export default MyBranchSwitcher;
